import { useMemo } from "react";
import {
  Client,
  TimeEntry,
  useTimeTrackerStore,
  WorkdaysPerWeek,
} from "../store/timeTrackerStore";
import {
  getCompletedWeekRange,
  getCurrentMonthRange,
  getCurrentWeekRange,
  getWeekPaceFraction,
  sumHoursByClient,
} from "../utils/analyticsPeriods";
import { formatDays, hoursToDays } from "../utils/billableDays";
import {
  smoothEntries,
  summarizeSmoothing,
  SmoothingSummary,
} from "../utils/smoothing";
import { DATE_PRESETS, DatePreset } from "../utils/TimeTrackerUtils";
import {
  enumCodec,
  setUrlParams,
  stringSetCodec,
} from "../utils/useUrlState";
import { modalCodec } from "../utils/modalParam";
import { Card, MetricRow } from "@lazar/ui";

// Codecs mirror the ones in TimeTracker — keys must match for the deep-link to
// land cleanly. Defaults match (modalCodec default "none", enumCodec default
// "week", empty Set) so serialising the defaults yields `null` and the URL
// stays clean.
const datePresetCodec = enumCodec(DATE_PRESETS, "week");
const clientsCodec = stringSetCodec();

const isDrillable = (id: string) => id !== "unassigned";

// Single deep-link primitive used by every clickable analytics row. Batches the
// URL update so the back button restores the prior analytics view in one step.
const drillDown = (preset: DatePreset, clientId: string) => {
  if (!isDrillable(clientId)) return;
  setUrlParams({
    modal: modalCodec.serialize("none"),
    date: datePresetCodec.serialize(preset),
    clients: clientsCodec.serialize(new Set([clientId])),
  });
};

const fmtHours = (h: number) => `${h.toFixed(1)}h`;

const MS_PER_HOUR = 3_600_000;

// formatDays/fmtHours render an unsigned magnitude, so the sign is chosen here.
// Reclaimed time is normally positive, but overlapping raw entries de-duped by
// smoothing can net negative — these keep that reading as "-0.1h" rather than
// the nonsensical "+-0.1h".
const signedDays = (hours: number, dayHours: number): string =>
  `${hours < 0 ? "-" : "+"}${formatDays(hoursToDays(Math.abs(hours), dayHours))}`;
const signedHours = (hours: number): string =>
  `${hours < 0 ? "-" : "+"}${fmtHours(Math.abs(hours))}`;

interface ClientView {
  id: string;
  name: string;
  color: string;
  targetDays?: number;
}

const clientView = (
  id: string,
  getClient: (id: string) => Client | undefined,
): ClientView => {
  if (id === "unassigned") {
    return { id, name: "No client", color: "#9CA3AF" };
  }
  const c = getClient(id);
  const baseName = c?.name ?? "Unknown";
  return {
    id,
    name: c?.archivedAt !== undefined ? `${baseName} (archived)` : baseName,
    color: c?.color ?? "#9CA3AF",
    targetDays: c?.weeklyTargetDays,
  };
};

interface PacingRow extends ClientView {
  hours: number;
  pct?: number;
}

interface BillingRow extends ClientView {
  lastWeek: number;
  thisWeek: number;
  thisMonth: number;
}

// Reads the store and computes every analytics panel's data in one place, so
// the modal that consumes it stays thin. `now` is captured once per mount so
// the panels stay consistent with each other; the modal remounts this on each
// open, which keeps `now` fresh without recomputing while closed.
export const useAnalyticsData = () => {
  const { timeEntries, clients, getClient, settings } = useTimeTrackerStore();
  const dayHours = settings.dayHours;
  const workdaysPerWeek = settings.workdaysPerWeek;

  const now = useMemo(() => new Date(), []);
  const allEntries = useMemo(
    () => Object.values(timeEntries) as TimeEntry[],
    [timeEntries],
  );

  // When smoothing is on, every panel computes on the smoothed entries so the
  // dashboard reflects the same picture the entries list shows. smoothEntries
  // is completed-only (running entries are already skipped by sumHoursByClient),
  // and a SmoothedEntry is a TimeEntry, so the windowing below is unchanged.
  const smoothing = settings.smoothing;
  const thresholdMs = smoothing.thresholdMinutes * 60_000;
  const smoothed = useMemo(
    () => (smoothing.enabled ? smoothEntries(allEntries, thresholdMs) : null),
    [smoothing.enabled, thresholdMs, allEntries],
  );
  const analyticsEntries: TimeEntry[] = smoothed ?? allEntries;
  // Two scopes, so the panel can show lifetime impact next to what landed this
  // week. The this-week figure windows by block startTime to match
  // sumHoursByClient (which Pacing uses), so a client's this-week reclaimed
  // equals the delta you see when toggling smoothing on the "This week" tab.
  const smoothingSummary = useMemo(() => {
    if (!smoothed) return null;
    const week = getCurrentWeekRange(now);
    const weekStart = week.start.getTime();
    const weekEnd = week.end.getTime();
    return {
      allTime: summarizeSmoothing(smoothed),
      thisWeek: summarizeSmoothing(
        smoothed.filter(
          (s) => s.startTime >= weekStart && s.startTime < weekEnd,
        ),
      ),
    };
  }, [smoothed, now]);

  const pacing = useMemo(() => {
    const range = getCurrentWeekRange(now);
    const byClient = sumHoursByClient(
      analyticsEntries,
      range.start.getTime(),
      range.end.getTime(),
    );
    const paceFrac = getWeekPaceFraction(now, workdaysPerWeek);
    // Surface clients with a weekly target even when they have zero tracked
    // hours this week — those are the ones most behind pace and need a nudge.
    // Clients without a target only appear if they have tracked time.
    const ids = new Set<string>(Object.keys(byClient));
    // Active clients only — archived clients with a stale weekly target
    // shouldn't keep padding the pacing panel after they're gone.
    Object.values(clients).forEach((c) => {
      if (c.archivedAt !== undefined) return;
      if (c.weeklyTargetDays !== undefined) ids.add(c.id);
    });
    const rows: PacingRow[] = Array.from(ids).map((id) => {
      const view = clientView(id, getClient);
      const hours = byClient[id] ?? 0;
      // Target lives in days; compare tracked hours against its hours
      // equivalent so the pct holds however the day is calibrated.
      const pct = view.targetDays
        ? hours / (view.targetDays * dayHours)
        : undefined;
      return { ...view, hours, pct };
    });
    // Lagging first; clients without a target fall to the bottom, sorted by raw hours.
    rows.sort((a, b) => {
      if (a.pct === undefined && b.pct === undefined) return b.hours - a.hours;
      if (a.pct === undefined) return 1;
      if (b.pct === undefined) return -1;
      return a.pct - b.pct;
    });
    return { rows, paceFrac };
  }, [analyticsEntries, clients, getClient, now, workdaysPerWeek, dayHours]);

  const billing = useMemo(() => {
    const lw = getCompletedWeekRange(now, 1);
    const tw = getCurrentWeekRange(now);
    const tm = getCurrentMonthRange(now);
    const lwSums = sumHoursByClient(analyticsEntries, lw.start.getTime(), lw.end.getTime());
    const twSums = sumHoursByClient(analyticsEntries, tw.start.getTime(), tw.end.getTime());
    const tmSums = sumHoursByClient(analyticsEntries, tm.start.getTime(), tm.end.getTime());
    const ids = new Set<string>([
      ...Object.keys(lwSums),
      ...Object.keys(twSums),
      ...Object.keys(tmSums),
    ]);
    const rows: BillingRow[] = Array.from(ids).map((id) => {
      const view = clientView(id, getClient);
      return {
        ...view,
        lastWeek: lwSums[id] ?? 0,
        thisWeek: twSums[id] ?? 0,
        thisMonth: tmSums[id] ?? 0,
      };
    });
    rows.sort((a, b) => b.thisMonth - a.thisMonth);
    const totals = rows.reduce(
      (acc, r) => ({
        lastWeek: acc.lastWeek + r.lastWeek,
        thisWeek: acc.thisWeek + r.thisWeek,
        thisMonth: acc.thisMonth + r.thisMonth,
      }),
      { lastWeek: 0, thisWeek: 0, thisMonth: 0 },
    );
    return { rows, totals };
  }, [analyticsEntries, getClient, now]);

  return {
    hasEntries: allEntries.length > 0,
    dayHours,
    workdaysPerWeek,
    getClient,
    now,
    pacing,
    billing,
    smoothingSummary,
  };
};

export interface SmoothingScopes {
  allTime: SmoothingSummary;
  thisWeek: SmoothingSummary;
}

// What smoothing reclaimed, in two scopes: lifetime impact and what landed this
// week. The this-week column matches the delta you'd see toggling smoothing on
// the "This week" tab, so the big lifetime number stops reading as a this-week
// figure. Shown only when smoothing is enabled. The per-client list skips
// clients whose totals are both unchanged (no gaps absorbed either scope) so it
// reads as a "smoothing impact" panel rather than a duplicate client list.
export const SmoothingSummaryPanel = ({
  summary,
  getClient,
  dayHours,
}: {
  summary: SmoothingScopes;
  getClient: (id: string) => Client | undefined;
  dayHours: number;
}) => {
  const { allTime, thisWeek } = summary;
  const allTimeHours = allTime.reclaimedMsTotal / MS_PER_HOUR;
  const thisWeekHours = thisWeek.reclaimedMsTotal / MS_PER_HOUR;

  const ids = new Set<string>([
    ...Object.keys(allTime.byClient),
    ...Object.keys(thisWeek.byClient),
  ]);
  const rows = Array.from(ids)
    .map((id) => ({
      view: clientView(id, getClient),
      allTimeHours: (allTime.byClient[id]?.reclaimedMs ?? 0) / MS_PER_HOUR,
      thisWeekHours: (thisWeek.byClient[id]?.reclaimedMs ?? 0) / MS_PER_HOUR,
    }))
    .filter((r) => r.allTimeHours !== 0 || r.thisWeekHours !== 0)
    .sort((a, b) => b.allTimeHours - a.allTimeHours);

  return (
    <Card className="p-6">
      <div className="flex items-baseline justify-between mb-1">
        <h2 className="text-lg font-black dark:text-cream">Smoothing</h2>
        <span className="text-xs font-bold text-ink-soft dark:text-cream/70">
          {allTime.gapsClosed} gap{allTime.gapsClosed === 1 ? "" : "s"} merged
          all time
        </span>
      </div>
      <p className="text-xs text-ink-soft dark:text-cream/60 mb-4">
        Time folded back in from short between-task breaks. It's already added
        to every panel below — your raw entries stay unchanged.
      </p>
      <div className="flex items-baseline justify-between gap-4 mb-4">
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-black text-ink dark:text-cream">
            {signedDays(allTimeHours, dayHours)}
          </span>
          <span className="text-sm text-ink-soft dark:text-cream/70 font-mono">
            ({signedHours(allTimeHours)}) all time
          </span>
        </div>
        <span className="text-sm text-ink-soft dark:text-cream/70 font-mono flex-shrink-0">
          {signedHours(thisWeekHours)} this week
        </span>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-ink-soft dark:text-cream/70">
          No gaps were smoothed — every break is longer than your threshold.
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <Card sub key={row.view.id} className="text-sm p-3">
              <MetricRow
                leadingClassName="pr-2"
                leading={
                  <>
                    <div
                      className="w-3 h-3 rounded-full mr-2 flex-shrink-0 border-2 border-ink dark:border-cream"
                      style={{ backgroundColor: row.view.color }}
                    />
                    <span className="text-ink dark:text-cream font-bold truncate">
                      {row.view.name}
                    </span>
                  </>
                }
                valueClassName="flex items-baseline gap-3 font-mono text-xs"
                value={
                  <>
                    <span className="text-ink dark:text-cream">
                      {signedDays(row.allTimeHours, dayHours)} (
                      {signedHours(row.allTimeHours)})
                    </span>
                    <span className="text-ink-soft dark:text-cream/60 w-20 text-right">
                      {signedHours(row.thisWeekHours)} wk
                    </span>
                  </>
                }
              />
            </Card>
          ))}
        </div>
      )}
    </Card>
  );
};

const WORKWEEK_LABEL: Record<WorkdaysPerWeek, string> = {
  4: "Mon–Thu",
  5: "Mon–Fri",
  6: "Mon–Sat",
  7: "Mon–Sun",
};

export const PacingPanel = ({
  rows,
  paceFrac,
  dayHours,
  workdaysPerWeek,
  now,
}: {
  rows: PacingRow[];
  paceFrac: number;
  dayHours: number;
  workdaysPerWeek: WorkdaysPerWeek;
  now: Date;
}) => {
  const pacePct = Math.round(paceFrac * 100);
  const dow = now.getDay();
  // Mon=1…Sat=6, Sun→7 so it sorts after Sat in a Mon-anchored ordering.
  const dayIndex = dow === 0 ? 7 : dow;
  const isOffDay = dayIndex > workdaysPerWeek;
  const workweekLabel = WORKWEEK_LABEL[workdaysPerWeek];
  return (
    <Card className="p-6">
      <div className="flex items-baseline justify-between mb-1">
        <h2 className="text-lg font-black dark:text-cream">
          This week — pacing
        </h2>
        <span className="text-xs font-bold text-ink-soft dark:text-cream/70">
          By end of today: {pacePct}%
          {isOffDay ? " (work week done)" : ""}
        </span>
      </div>
      <p className="text-xs text-ink-soft dark:text-cream/60 mb-4">
        Where to point your next hour. The top row is the client furthest
        behind its weekly target — start there to stay on plan. The black
        tick on each bar marks where an even {workweekLabel} pace would
        have you by end of today.
      </p>
      {rows.length === 0 ? (
        <p className="text-sm text-ink-soft dark:text-cream/70">
          No time tracked yet this week.
        </p>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <PacingBar
              key={row.id}
              row={row}
              paceFrac={paceFrac}
              dayHours={dayHours}
            />
          ))}
        </div>
      )}
    </Card>
  );
};

const PacingBar = ({
  row,
  paceFrac,
  dayHours,
}: {
  row: PacingRow;
  paceFrac: number;
  dayHours: number;
}) => {
  const hasTarget = row.targetDays !== undefined;
  // The bar scale is in hours (tracked time is hours); convert the day target.
  const targetHours = (row.targetDays ?? 0) * dayHours;
  // Per-row scale: max(target, hours) so over-target rows still show a full bar.
  const scaleMax = hasTarget ? Math.max(targetHours, row.hours) : 0;
  const fillPct = scaleMax > 0 ? (row.hours / scaleMax) * 100 : 0;
  const markPct = scaleMax > 0 ? ((targetHours * paceFrac) / scaleMax) * 100 : 0;

  let fillColor = "bg-sky-brut";
  if (hasTarget && row.pct !== undefined) {
    if (row.pct >= 1) {
      fillColor = "bg-zest-500";
    } else if (row.pct < paceFrac - 0.05) {
      fillColor = "bg-coral-500";
    } else {
      fillColor = "bg-lemon-500";
    }
  }

  const days = formatDays(hoursToDays(row.hours, dayHours));
  const targetDays = hasTarget ? formatDays(row.targetDays ?? 0) : null;

  const inner = (
    <>
      <div className="flex items-center justify-between text-sm mb-1">
        <div className="flex items-center min-w-0 flex-1 pr-2">
          <div
            className="w-3 h-3 rounded-full mr-2 flex-shrink-0 border-2 border-ink dark:border-cream"
            style={{ backgroundColor: row.color }}
          />
          <span className="text-ink dark:text-cream font-bold truncate">
            {row.name}
          </span>
        </div>
        <span className="text-ink-soft dark:text-cream/70 font-mono text-xs flex-shrink-0">
          {hasTarget
            ? `${days} / ${targetDays} (${fmtHours(row.hours)} / ${fmtHours(targetHours)}) · ${Math.round((row.pct ?? 0) * 100)}%`
            : `${days} (${fmtHours(row.hours)})`}
        </span>
      </div>
      <div className="relative h-4 border-2 border-ink dark:border-cream bg-lemon-50 dark:bg-charcoal overflow-hidden">
        {hasTarget && (
          <>
            <div
              className={`h-full ${fillColor}`}
              style={{ width: `${Math.min(fillPct, 100)}%` }}
            />
            <div
              className="absolute top-0 bottom-0 w-1 bg-ink dark:bg-cream"
              style={{ left: `${Math.min(markPct, 100)}%` }}
              title="Expected pace"
            />
          </>
        )}
      </div>
    </>
  );

  if (!isDrillable(row.id)) {
    return <div className="p-1">{inner}</div>;
  }
  return (
    <button
      type="button"
      onClick={() => drillDown("week", row.id)}
      aria-label={`Filter entries to ${row.name} for this week`}
      className="block w-full text-left p-1 rounded cursor-pointer hover:bg-lemon-100 dark:hover:bg-charcoal transition-colors"
    >
      {inner}
    </button>
  );
};

const BillingCell = ({
  hours,
  dayHours,
  emphasis = false,
  onClick,
  ariaLabel,
}: {
  hours: number;
  dayHours: number;
  emphasis?: boolean;
  onClick?: () => void;
  ariaLabel?: string;
}) => {
  const primary = emphasis
    ? "text-ink dark:text-cream font-black"
    : "text-ink dark:text-cream/90 font-bold";
  const inner = (
    <>
      <div className={primary}>{formatDays(hoursToDays(hours, dayHours))}</div>
      <div className="text-xs text-ink-soft dark:text-cream/50">
        ({fmtHours(hours)})
      </div>
    </>
  );
  if (!onClick) {
    return <div className="text-right font-mono">{inner}</div>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className="text-right font-mono w-full p-1 rounded cursor-pointer hover:bg-lemon-100 dark:hover:bg-charcoal transition-colors"
    >
      {inner}
    </button>
  );
};

export const BillingPanel = ({
  rows,
  totals,
  dayHours,
}: {
  rows: BillingRow[];
  totals: { lastWeek: number; thisWeek: number; thisMonth: number };
  dayHours: number;
}) => (
  <Card className="p-6">
    <h2 className="text-lg font-black mb-1 dark:text-cream">Billing summary</h2>
    <p className="text-xs text-ink-soft dark:text-cream/60 mb-4">
      Your in-progress invoice picture. Last week, this week, and the month
      so far — side by side. Useful for end-of-month reconciliation and for
      catching any client whose month is running unusually light or heavy.
    </p>
    {rows.length === 0 ? (
      <p className="text-sm text-ink-soft dark:text-cream/70">
        No billable time yet.
      </p>
    ) : (
      <table className="w-full text-sm">
        <thead>
          <tr className="text-ink-soft dark:text-cream/70 text-xs uppercase tracking-wider">
            <th className="font-black text-left pb-2">Client</th>
            <th className="font-black text-right pb-2">Last week</th>
            <th className="font-black text-right pb-2">This week</th>
            <th className="font-black text-right pb-2">This month</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const drillable = isDrillable(row.id);
            return (
              <tr
                key={row.id}
                className="border-t-2 border-ink/20 dark:border-cream/20"
              >
                <td className="py-2 align-top">
                  <div className="flex items-center min-w-0">
                    <div
                      className="w-3 h-3 rounded-full mr-2 flex-shrink-0 border-2 border-ink dark:border-cream"
                      style={{ backgroundColor: row.color }}
                    />
                    <span className="text-ink dark:text-cream font-bold truncate">
                      {row.name}
                    </span>
                  </div>
                </td>
                <td className="py-2">
                  <BillingCell
                    hours={row.lastWeek}
                    dayHours={dayHours}
                    onClick={
                      drillable ? () => drillDown("lastWeek", row.id) : undefined
                    }
                    ariaLabel={`Filter entries to ${row.name} for last week`}
                  />
                </td>
                <td className="py-2">
                  <BillingCell
                    hours={row.thisWeek}
                    dayHours={dayHours}
                    onClick={
                      drillable ? () => drillDown("week", row.id) : undefined
                    }
                    ariaLabel={`Filter entries to ${row.name} for this week`}
                  />
                </td>
                <td className="py-2">
                  <BillingCell
                    hours={row.thisMonth}
                    dayHours={dayHours}
                    onClick={
                      drillable
                        ? () => drillDown("thisMonth", row.id)
                        : undefined
                    }
                    ariaLabel={`Filter entries to ${row.name} for this month`}
                  />
                </td>
              </tr>
            );
          })}
          <tr className="border-t-[3px] border-ink dark:border-cream font-black">
            <td className="py-2 align-top text-ink dark:text-cream uppercase tracking-wider text-xs">
              Total
            </td>
            <td className="py-2">
              <BillingCell
                hours={totals.lastWeek}
                dayHours={dayHours}
                emphasis
              />
            </td>
            <td className="py-2">
              <BillingCell
                hours={totals.thisWeek}
                dayHours={dayHours}
                emphasis
              />
            </td>
            <td className="py-2">
              <BillingCell
                hours={totals.thisMonth}
                dayHours={dayHours}
                emphasis
              />
            </td>
          </tr>
        </tbody>
      </table>
    )}
  </Card>
);
